import { FetchApiError } from '@/utils/apiFetch';
import { getNestedBoolean, useConfig } from '@/utils/useConfig';
import useNavigate from '@fuse/hooks/useNavigate';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Checkbox, FormControl, FormControlLabel, FormHelperText } from '@mui/material';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { MuiTelInput } from 'mui-tel-input';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';
import { JwtSignUpPayload } from '../JwtAuthProvider';
import useJwtAuth from '../useJwtAuth';
import SignUpSocialButtons from './SignUpSocialButtons';

/**
 * Form Validation Schema
 */
type TelInputProps = {
	label: string;
	value: string;
	onChange: (value: string) => void;
	helpText?: string;
	disabled?: boolean;
	error?: boolean;
};

const TelInput: React.FC<TelInputProps> = ({ label, value, onChange, helpText, disabled = false, error = false }) => {
	return (
		<MuiTelInput
			label={label}
			value={value}
			onChange={onChange}
			variant="outlined"
			fullWidth
			helperText={helpText}
			disabled={disabled}
			defaultCountry="ZA"
			error={error}
			sx={{
				'& .MuiOutlinedInput-root': {
					borderRadius: '0.75rem',
				}
			}}
		/>
	);
};

const createSchema = (showJobTitle: boolean, showTradingName: boolean, showTermsPrivacy: boolean) => z.object({
	first: z.string().min(1, 'You must enter your first name'),
	last: z.string().min(1, 'You must enter your last name'),
	email: z
		.string()
		.transform((val) => val.trim() === '' ? undefined : val)
		.optional()
		.refine(
			(val) => val === undefined || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
			'You must enter a valid email'
		),
	passwd: z.string()
		.min(1, 'New password is required')
		.min(8, 'Password must be at least 8 characters')
		.regex(
			/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*+])[A-Za-z\d!@#$%^&*+]{8,}$/,
			'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
		),
	passwordConfirm: z.string().min(1, 'Password confirmation is required'),
	acceptTermsConditions: showTermsPrivacy
		? z.boolean().refine((val) => val === true, 'The terms and conditions must be accepted.')
		: z.boolean(),
	rar: showTradingName ? z.string().min(1, 'You must enter a trading name') : z.string(),
	jobtitle: showJobTitle ? z.string().min(1, 'You must enter a job title') : z.string(),
	tel: z.string().min(5, 'Phone number is required'),
})
	.refine((data) => data.passwd === data.passwordConfirm, {
		message: 'Passwords must match',
		path: ['passwordConfirm'],
	});

type FormType = JwtSignUpPayload & {
	passwordConfirm: string;
	acceptTermsConditions: boolean;
};

const defaultValues = {
	first: '',
	last: '',
	email: '',
	passwd: '',
	passwordConfirm: '',
	rar: '',
	jobtitle: '',
	tel: '',
	acceptTermsConditions: false
};


function JwtSignUpForm({ config, social }) {

	const navigate = useNavigate();
	const showJobTitle = getNestedBoolean(config, 'signUp.displayJobTitle');
	const showTradingName = getNestedBoolean(config, 'signUp.displayTradingName');
	const { signUp } = useJwtAuth();
	const { signUpWithSocials } = useJwtAuth();
	const { config: termsConfig } = useConfig('terms_config');
	const { config: privacyConfig } = useConfig('privacy_config');
	const termsEnabled = getNestedBoolean(termsConfig, 'enable');
	const privacyEnabled = getNestedBoolean(privacyConfig, 'enable');
	const showTermsPrivacy = termsEnabled || privacyEnabled;
	const termsPrivacyLabel =
		termsEnabled && privacyEnabled ? 'I agree with the Terms of Service and Privacy Policy' :
		termsEnabled ? 'I agree with the Terms of Service' :
		'I agree with the Privacy Policy';

	const baseSchema = createSchema(showJobTitle, showTradingName, showTermsPrivacy);

	const schema = baseSchema.superRefine((data, ctx) => {
		if (social === null && (!data.email || data.email.trim() === '')) {
			ctx.addIssue({
				path: ['email'],
				code: z.ZodIssueCode.custom,
				message: 'You must enter an email if social is not connected',
			});
		}
	});

	const { control, formState, handleSubmit, setError, trigger } = useForm<FormType>({
		mode: 'onChange',
		defaultValues,
		resolver: zodResolver(schema)
	});

	const { isValid: _isValid, dirtyFields: _dirtyFields, errors } = formState;

	async function onSubmit(formData: FormType) {
		const { first, last, rar, jobtitle, email, passwd, tel } = formData;
		let formatted_tel: string;

		const countryCodeMatch = tel.match(/^\+(\d+)\s/);


		if (countryCodeMatch?.[1]) {
			// Format phone number with dot separator (e.g., +27.123456789)
			const countryCode = countryCodeMatch[1];

			// Remove all non-digit characters to get clean number
			const allDigits = tel.replace(/\D/g, '');

			// Extract phone number without country code
			const phoneNumber = allDigits.substring(countryCode.length);

			// Create formatted string with dot separator
			formatted_tel = `+${countryCode}.${phoneNumber}`;


		} else {
			// Fallback: save only digits if country code extraction fails
			formatted_tel = tel.replace(/\D/g, '');

		}

		if (social) {
			signUpWithSocials({ first, last, tel: formatted_tel, rar, jobtitle, passwd, app_url: globalThis.window.location.origin, token: social.token, provider: social.provider, local: social.local })
				.catch((err) => {
					console.warn('Social sign-up error:', err);
				})
				.finally(() => {
					globalThis.window.history.replaceState({}, document.title, globalThis.window.location.pathname);
				});
		} else {

			signUp({
				first, last, tel: formatted_tel, rar, jobtitle, passwd, email, app_url: globalThis.window.location.origin
			})
				.then(() => {
					navigate('/sign-in');
					toast.success('Email verification has been sent with next instructions');
				})
				.catch((error: FetchApiError) => {
					// eslint-disable-next-line no-console
					console.error(error);
					const errorData = error.data as {
						type: 'email' | 'passwd' | `root.${string}` | 'root';
						message: string;
					}[];

					errorData?.forEach?.(({ message, type }) => {
						setError(type, { type: 'manual', message });
					});

					if (error.data['non_field_errors']) {
						error.data['non_field_errors'].map((err) => {
							toast.error(err);
						});

					}

				});
		}


	}

	return (
		<>
			{social && <Alert severity="info" className='mt-24'>You are signing up using social authentication. Please complete the form to create your account.</Alert>}

			<form
				name="registerForm"
				noValidate
				className="mt-32 flex w-full flex-col justify-center"
				onSubmit={handleSubmit(onSubmit, (invalid) => {
					console.warn('Form invalid:', invalid); // catch silent validation fails
				})}
			>
				{showTradingName && <Controller
					name="rar"
					control={control}
					render={({ field }) => (
						<TextField
							{...field}
							className="mb-24"
							label="Trading Name"
							type="name"
							error={!!errors.rar}
							helperText={errors?.rar?.message}
							variant="outlined"
							required
							fullWidth
						/>
					)}
				/>}

				{showJobTitle && <Controller
					name="jobtitle"
					control={control}
					render={({ field }) => (
						<TextField
							{...field}
							className="mb-24"
							label="Job title"
							type="name"
							error={!!errors.jobtitle}
							helperText={errors?.jobtitle?.message}
							variant="outlined"
							required
							fullWidth
						/>
					)}
				/>}

				<Controller
					name="first"
					control={control}
					render={({ field }) => (
						<TextField
							{...field}
							className="mb-24"
							label="First name"
							type="name"
							error={!!errors.first}
							helperText={errors?.first?.message}
							variant="outlined"
							required
							fullWidth
						/>
					)}
				/>
				<Controller
					name="last"
					control={control}
					render={({ field }) => (
						<TextField
							{...field}
							className="mb-24"
							label="Last name"
							type="name"
							error={!!errors.last}
							helperText={errors?.last?.message}
							variant="outlined"
							required
							fullWidth
						/>
					)}
				/>

				{!social && <Controller
					name="email"
					control={control}
					render={({ field }) => (
						<TextField
							{...field}
							className="mb-24"
							label="Email"
							type="email"
							error={!!errors.email}
							helperText={errors?.email?.message}
							variant="outlined"
							fullWidth
							required
						/>
					)}
				/>}

				<div className="mb-24">

					<Controller
						name="tel"
						control={control}
						render={({ field }) => (
							<TelInput
								label="Phone Number *"
								value={field.value}
								onChange={field.onChange}
								helpText={errors?.tel?.message || 'Enter your mobile number'}
								error={!!errors.tel}

							/>
						)}
					/>

				</div>

				<Controller
					name="passwd"
					control={control}
					render={({ field }) => (
						<TextField
							{...field}
							className="mb-24"
							label="Password"
							type="password"
							error={!!errors.passwd}
							helperText={errors?.passwd?.message}
							variant="outlined"
							required
							fullWidth
							onPaste={() => {
								// Retrigger validation after paste
								setTimeout(() => trigger('passwd'), 0);
							}}
						/>
					)}
				/>

				<Controller
					name="passwordConfirm"
					control={control}
					render={({ field }) => (
						<TextField
							{...field}
							className="mb-24"
							label="Password (Confirm)"
							type="password"
							error={!!errors.passwordConfirm}
							helperText={errors?.passwordConfirm?.message}
							variant="outlined"
							required
							fullWidth
							onPaste={() => {
								// Retrigger validation after paste
								setTimeout(() => trigger(['passwd', 'passwordConfirm']), 0);
							}}
						/>
					)}
				/>

				{showTermsPrivacy && <Controller
					name="acceptTermsConditions"
					control={control}
					render={({ field }) => (
						<FormControl error={!!errors.acceptTermsConditions}>
							<FormControlLabel
								label={termsPrivacyLabel}
								control={
									<Checkbox
										size="small"
										{...field}
									/>
								}
							/>
							<FormHelperText>{errors?.acceptTermsConditions?.message}</FormHelperText>
						</FormControl>
					)}
				/>}

				<Button
					variant="contained"
					color="secondary"
					className="mt-24 w-full"
					aria-label="Register"
					// disabled={!isValid}
					type="submit"
					size="large"
				>
					Create your account
				</Button>

				{!social && <SignUpSocialButtons disabled={false} />}
			</form>
		</>
	);
}

export default JwtSignUpForm;
